// src/pages/Casting/components/SurfaceSetupCard.jsx

import React, { useState } from "react";
import {
  FaCheck,
  FaLayerGroup,
  FaMinus,
  FaPlus,
} from "react-icons/fa";

const PRESET_COUNTS = [2, 4, 6, 8, 10, 12];
const MIN_SURFACES = 1;
const MAX_SURFACES = 30;

export default function SurfaceSetupCard({
  loading = false,
  onInitialize,
}) {
  const [count, setCount] = useState(6);

  const updateCount = (nextValue) => {
    const numericValue = Number(nextValue);

    if (!Number.isFinite(numericValue)) {
      setCount(MIN_SURFACES);
      return;
    }

    setCount(
      Math.min(
        MAX_SURFACES,
        Math.max(MIN_SURFACES, numericValue)
      )
    );
  };

  const handleSubmit = async () => {
    if (loading || typeof onInitialize !== "function") {
      return;
    }

    await onInitialize(count);
  };

  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
      <div className="border-b border-slate-200 bg-gradient-to-br from-sky-50 via-white to-indigo-50 px-5 py-6 dark:border-white/10 dark:from-sky-950/30 dark:via-slate-900 dark:to-indigo-950/20 sm:px-7">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-sky-600 text-xl text-white shadow-lg shadow-sky-600/20">
            <FaLayerGroup />
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-sky-700 dark:text-sky-300">
              Configuration du dojo
            </p>

            <h2 className="mt-1 text-xl font-black text-slate-950 dark:text-white sm:text-2xl">
              Combien de surfaces sont utilisées ?
            </h2>

            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Les tapis seront créés automatiquement. Tu pourras ensuite
              modifier leur nom, leur type et leur ordre d’affichage.
              Y inclure la Table centrale et Podium si ils se trouvent sur une des surfaces du Dojo.
            </p>
          </div>
        </div>
      </div>

      <div className="px-5 py-6 sm:px-7">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            Choix rapide
          </p>

          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
            {PRESET_COUNTS.map((preset) => {
              const isSelected = count === preset;

              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setCount(preset)}
                  disabled={loading}
                  className={[
                    "relative flex h-14 items-center justify-center rounded-2xl border text-base font-black transition",
                    "disabled:cursor-not-allowed disabled:opacity-60",
                    isSelected
                      ? "border-sky-600 bg-sky-600 text-white shadow-lg shadow-sky-600/20"
                      : "border-slate-200 bg-slate-50 text-slate-800 hover:border-sky-300 hover:bg-sky-50 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-sky-500/10",
                  ].join(" ")}
                >
                  {preset}

                  {isSelected ? (
                    <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[8px] text-sky-700">
                      <FaCheck />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6">
          <label
            htmlFor="surface-count"
            className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400"
          >
            Nombre personnalisé
          </label>

          <div className="mt-3 flex max-w-sm items-center gap-2">
            <button
              type="button"
              onClick={() => updateCount(count - 1)}
              disabled={loading || count <= MIN_SURFACES}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              aria-label="Retirer une surface"
            >
              <FaMinus />
            </button>

            <div className="relative flex-1">
              <input
                id="surface-count"
                type="number"
                min={MIN_SURFACES}
                max={MAX_SURFACES}
                value={count}
                disabled={loading}
                onChange={(event) => updateCount(event.target.value)}
                className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-center text-lg font-black text-slate-950 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />

              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold uppercase text-slate-400">
                surfaces
              </span>
            </div>

            <button
              type="button"
              onClick={() => updateCount(count + 1)}
              disabled={loading || count >= MAX_SURFACES}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
              aria-label="Ajouter une surface"
            >
              <FaPlus />
            </button>
          </div>

          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Entre {MIN_SURFACES} et {MAX_SURFACES} surfaces.
          </p>
        </div>

        <div className="mt-7 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-6 py-3 text-sm font-black text-white shadow-lg shadow-sky-600/20 transition hover:-translate-y-0.5 hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          >
            <FaLayerGroup />

            {loading
              ? "Création des surfaces…"
              : `Créer ${count} surface${count > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </section>
  );
}